// ─────────────────────────────────────────────
//  StockHeader.js
//  Live price header for the Stock Analysis page.
//  Shows: price, change, 52w range, exchange toggle,
//  watchlist/portfolio quick-add buttons.
// ─────────────────────────────────────────────
import React, { useState, useEffect } from "react";
import { fetchQuote } from "../../config/api";
import { useWatchlist } from "../../hooks/useWatchlist";
import "./StockHeader.css";

export default function StockHeader({ symbol, exchange, onExchangeChange }) {
  const [quote,   setQuote]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const { items, addToWatchlist, removeFromWatchlist } = useWatchlist();

  const inWatchlist = items.some((i) => i.symbol === symbol && i.exchange === exchange);
  const watchlistItem = items.find((i) => i.symbol === symbol && i.exchange === exchange);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true); setError(null);
      try {
        const data = await fetchQuote(symbol, exchange);
        if (!cancelled) setQuote(data);
      } catch (e) {
        if (!cancelled) setError("Could not load quote.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    const id = setInterval(load, 5 * 60 * 1000);
    return () => { cancelled = true; clearInterval(id); };
  }, [symbol, exchange]);

  const change    = parseFloat(quote?.change)         || 0;
  const changePct = parseFloat(quote?.change_percent) || 0;
  const isUp      = change >= 0;

  // 52w range fill %
  const price   = parseFloat(quote?.price) || 0;
  const low52   = parseFloat(quote?.year_low)  || price * 0.8;
  const high52  = parseFloat(quote?.year_high) || price * 1.2;
  const rangePct = high52 > low52
    ? (((price - low52) / (high52 - low52)) * 100).toFixed(1)
    : 50;

  async function toggleWatchlist() {
    if (inWatchlist) {
      await removeFromWatchlist(watchlistItem.id);
    } else {
      await addToWatchlist({ symbol, exchange, notes: "" });
    }
  }

  return (
    <div className="sh-wrap">
      {/* Left: symbol + name */}
      <div className="sh-left">
        <div className="sh-symbol">{symbol}</div>
        {quote?.name && <div className="sh-name">{quote.name}</div>}

        {/* Exchange toggle */}
        <div className="sh-exchange-toggle">
          {["NSE", "BSE"].map((ex) => (
            <button
              key={ex}
              className={`sh-exch-btn ${exchange === ex ? "active" : ""}`}
              onClick={() => onExchangeChange(ex)}
            >
              {ex}
            </button>
          ))}
        </div>
      </div>

      {/* Centre: price */}
      <div className="sh-centre">
        {loading ? (
          <div className="sh-price-skel" />
        ) : error ? (
          <div className="sh-error">⚠ {error}</div>
        ) : (
          <>
            <div className="sh-price">
              ₹{Number(price).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            <div className={`sh-change ${isUp ? "up" : "down"}`}>
              <span>{isUp ? "▲" : "▼"}</span>
              {Math.abs(change).toFixed(2)}
              <span className="sh-pct">({Math.abs(changePct).toFixed(2)}%)</span>
              <span className="sh-day-label">today</span>
            </div>
          </>
        )}
      </div>

      {/* Right: stats + actions */}
      <div className="sh-right">
        {quote && !loading && (
          <div className="sh-stats">
            <div className="sh-stat">
              <span className="sh-stat-label">Open</span>
              <span className="sh-stat-val">₹{Number(quote.open||0).toLocaleString("en-IN",{maximumFractionDigits:2})}</span>
            </div>
            <div className="sh-stat">
              <span className="sh-stat-label">High</span>
              <span className="sh-stat-val text-green">₹{Number(quote.high||0).toLocaleString("en-IN",{maximumFractionDigits:2})}</span>
            </div>
            <div className="sh-stat">
              <span className="sh-stat-label">Low</span>
              <span className="sh-stat-val text-red">₹{Number(quote.low||0).toLocaleString("en-IN",{maximumFractionDigits:2})}</span>
            </div>
            <div className="sh-stat">
              <span className="sh-stat-label">Prev Close</span>
              <span className="sh-stat-val">₹{Number(quote.previous_close||0).toLocaleString("en-IN",{maximumFractionDigits:2})}</span>
            </div>
            <div className="sh-stat">
              <span className="sh-stat-label">Volume</span>
              <span className="sh-stat-val">{fmtVol(quote.volume)}</span>
            </div>

            {/* 52-week range */}
            <div className="sh-stat sh-stat-full">
              <span className="sh-stat-label">52W Range</span>
              <div className="sh-range-wrap">
                <span className="sh-range-low">₹{Number(low52).toLocaleString("en-IN",{maximumFractionDigits:0})}</span>
                <div className="sh-range-track">
                  <div className="sh-range-fill" style={{ left: `${rangePct}%` }} />
                </div>
                <span className="sh-range-high">₹{Number(high52).toLocaleString("en-IN",{maximumFractionDigits:0})}</span>
              </div>
            </div>
          </div>
        )}

        {/* Action buttons */}
        <div className="sh-actions">
          <button
            className={`sh-btn-watch ${inWatchlist ? "active" : ""}`}
            onClick={toggleWatchlist}
            title={inWatchlist ? "Remove from Watchlist" : "Add to Watchlist"}
          >
            {inWatchlist ? "★ Watching" : "☆ Watchlist"}
          </button>
        </div>
      </div>
    </div>
  );
}

function fmtVol(v) {
  if (!v) return "—";
  const n = Number(v);
  if (n >= 1e7) return (n / 1e7).toFixed(2) + " Cr";
  if (n >= 1e5) return (n / 1e5).toFixed(2) + " L";
  if (n >= 1e3) return (n / 1e3).toFixed(1) + " K";
  return n.toString();
}